import type { ITemplateTag, ITemplate } from "@/api/Templates";
import type { ITagGroup } from "@medoma/models/templates";

export type IGroupedTemplates = {
  groupId: string;
  groupName: string;
  sortingNumber?: number;
  templates: ITemplate[];
};

export function groupTemplatesByTag(
  allTemplates: ITemplate[],
  allGroupTags: ITemplateTag[],
): { groups: IGroupedTemplates[]; ungroupedTemplates: ITemplate[] } {
  const groupMap = new Map<
    string,
    {
      groupId: string;
      groupName: string;
      sortingNumber?: number;
      templates: ITemplate[];
    }
  >();
  const ungroupedTemplates: ITemplate[] = [];

  for (const groupTag of allGroupTags) {
    groupMap.set(groupTag.id, {
      groupId: groupTag.id,
      groupName: parseGroupDisplayName(groupTag.displayName),
      sortingNumber: parseGroupSorting(groupTag.displayName),
      templates: [],
    });
  }

  for (const template of allTemplates) {
    const groupTags = filterTagsByGroup(template.tags, "group");

    if (groupTags.length === 0) {
      ungroupedTemplates.push(template);
    } else {
      const groupTag = groupTags[0];
      if (groupTag) {
        const existing = groupMap.get(groupTag.id);
        if (existing) {
          existing.templates.push(template);
        } else {
          ungroupedTemplates.push(template);
        }
      }
    }
  }

  const groups = [...groupMap.values()].sort((a, b) => {
    const sortingA = a.sortingNumber;
    const sortingB = b.sortingNumber;

    // Groups with sorting numbers come first
    if (sortingA !== undefined && sortingB !== undefined) {
      return sortingA - sortingB;
    }
    if (sortingA !== undefined) {
      return -1; // a comes before b
    }
    if (sortingB !== undefined) {
      return 1; // b comes before a
    }
    // Both have no sorting number, sort alphabetically by name
    return a.groupName.localeCompare(b.groupName);
  });

  return { groups, ungroupedTemplates };
}

/** Extract the group prefix from a tag displayName, e.g. "group" from "group:Foo". Returns empty string if no group prefix. */
export function parseTagGroup(displayName: string): string {
  const parts = displayName.split(":");
  return parts.length > 1 ? (parts[0] ?? "") : "";
}

/** Extract the name part after ":" from a tag displayName, e.g. "Foo" from "group:Foo". Returns the full displayName if no ":" is present. */
export function parseTagName(displayName: string): string {
  const parts = displayName.split(":");
  return parts.length > 1 ? parts.slice(1).join(":") : displayName;
}

/** Extract the group display name from a group tag displayName, e.g. "Inskrivning" from "group:HospitalAtHome/Inskrivning" or "Group name" from "group:HospitalAtHome/1-Group name". Returns the full tag name if no "/" is present. */
export function parseGroupDisplayName(displayName: string): string {
  const tagName = parseTagName(displayName);
  const slashIndex = tagName.indexOf("/");
  if (slashIndex < 0) {
    return tagName;
  }
  const afterSlash = tagName.substring(slashIndex + 1);
  // Check if there's a sorting number prefix (format: "Number-GroupName")
  const hyphenIndex = afterSlash.indexOf("-");
  if (hyphenIndex >= 0) {
    // Check if the part before hyphen is a number
    const beforeHyphen = afterSlash.substring(0, hyphenIndex);
    if (/^\d+$/.test(beforeHyphen)) {
      // It's a sorting number, return the part after the hyphen
      return afterSlash.substring(hyphenIndex + 1);
    }
  }
  // No sorting number, return everything after the slash
  return afterSlash;
}

/** Extract the sorting number from a group tag displayName, e.g. 1 from "group:HospitalAtHome/1-Group name". Returns undefined if no sorting number is present. */
export function parseGroupSorting(displayName: string): number | undefined {
  const tagName = parseTagName(displayName);
  const slashIndex = tagName.indexOf("/");
  if (slashIndex < 0) {
    return undefined;
  }
  const afterSlash = tagName.substring(slashIndex + 1);
  const hyphenIndex = afterSlash.indexOf("-");
  if (hyphenIndex >= 0) {
    const beforeHyphen = afterSlash.substring(0, hyphenIndex);
    if (/^\d+$/.test(beforeHyphen)) {
      const sortingNumber = Number.parseInt(beforeHyphen, 10);
      return Number.isNaN(sortingNumber) ? undefined : sortingNumber;
    }
  }
  return undefined;
}

/** Filter tags by group */
export function filterTagsByGroup(
  tags: ReadonlyArray<ITemplateTag>,
  group: ITagGroup,
): ITemplateTag[] {
  return tags.filter((tag) => parseTagGroup(tag.displayName) === group);
}
